// @ts-nocheck
(function attachConfiguratorCore(root, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  } else {
    root.NoDrillConfiguratorCore = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function createConfiguratorCore() {
  'use strict';

  const FRACTIONS = [
    { eighths: 0, label: '' },
    { eighths: 1, label: '1/8' },
    { eighths: 2, label: '1/4' },
    { eighths: 3, label: '3/8' },
    { eighths: 4, label: '1/2' },
    { eighths: 5, label: '5/8' },
    { eighths: 6, label: '3/4' },
    { eighths: 7, label: '7/8' },
  ];

  function round(value, precision) {
    const factor = 10 ** (precision || 6);
    return Math.round(value * factor) / factor;
  }

  function normalizeMeasurement(value, increment) {
    const numericValue = Number(value);
    const numericIncrement = Number(increment);

    if (!Number.isFinite(numericValue) || !Number.isFinite(numericIncrement) || numericIncrement <= 0) {
      return NaN;
    }

    return round(Math.round(numericValue / numericIncrement) * numericIncrement, 6);
  }

  function isOnIncrement(value, increment) {
    const normalized = normalizeMeasurement(value, increment);
    return Number.isFinite(normalized) && Math.abs(Number(value) - normalized) < 0.000001;
  }

  function dimensionLabel(dimension) {
    return dimension === 'height' ? 'Height' : 'Width';
  }

  function incrementLabel(increment) {
    if (Math.abs(Number(increment) - 0.125) < 0.000001) return '1/8-inch steps';
    if (Math.abs(Number(increment) - 0.25) < 0.000001) return '1/4-inch steps';
    if (Math.abs(Number(increment) - 0.5) < 0.000001) return '1/2-inch steps';
    return `${increment}-inch steps`;
  }

  function validateMeasurement(value, dimension, measurementRules) {
    measurementRules = applyStorefrontSizeCaps(measurementRules);
    const numericValue = Number(value);
    const label = dimensionLabel(dimension);
    const min = dimension === 'height' ? measurementRules.minHeight : measurementRules.minWidth;
    const max = dimension === 'height' ? measurementRules.maxHeight : measurementRules.maxWidth;

    if (!Number.isFinite(numericValue) || !Number.isFinite(min) || !Number.isFinite(max) || measurementRules.status === 'unresolved') {
      return { valid: false, error: `Pick the ${label.toLowerCase()}.` };
    }

    if (numericValue < min || numericValue > max) {
      return {
        valid: false,
        error: `Pick a ${label.toLowerCase()} from the list.`,
      };
    }

    if (!isOnIncrement(numericValue, measurementRules.increment)) {
      return {
        valid: false,
        error: `${label} must use ${incrementLabel(measurementRules.increment)}.`,
      };
    }

    return { valid: true, value: numericValue };
  }

  function roundToMeasurementIncrement(value, increment, direction) {
    const units = Number(value) / Number(increment);
    const rounded = direction === 'up' ? Math.ceil(units - 0.0000001) : Math.floor(units + 0.0000001);
    return Number((rounded * Number(increment)).toFixed(6));
  }

  // Store-wide size caps (NDR-D-040): width 500-2000 mm, height up to 2400 mm (94 3/8 in).
  // Per-control limits (cordless height 2200 mm, motorized width from 550 mm) live in
  // no-drill-category-config-v2.js confirmedSizeEnvelope and can only tighten these.
  // Customers only see sizes inside both; the size lists are the limits (NDR-D-093).
  // NDR-D-116 (Albert, Oct 19 2026): the smallest offered width leaves room for the largest inside-mount
  // width deduction (NDR-D-115: bumpy wall 1/4 in), so the finished shade never comes out under the
  // factory minimum. 500 mm -> 20 in (cordless), 550 mm -> 22 in (motorized). Prices are unchanged:
  // both stay in the same price band as the old 19 3/4 and 21 3/4 in minimums.
  const WIDTH_DEDUCTION_MARGIN_IN = 0.25;
  function minimumOfferedWidth(factoryMinimumMm, increment) {
    return roundToMeasurementIncrement(Number(factoryMinimumMm) / 25.4 + WIDTH_DEDUCTION_MARGIN_IN, increment, 'up');
  }

  function applyStorefrontSizeCaps(measurement) {
    const base = measurement || {};
    if (base.status === 'unresolved') return base;
    const increment = Number(base.increment || 0.125);
    return {
      ...base,
      minWidth: Math.max(Number(base.minWidth), minimumOfferedWidth(500, increment)),
      maxWidth: Math.min(Number(base.maxWidth), roundToMeasurementIncrement(2000 / 25.4, increment, 'down')),
      maxHeight: Math.min(Number(base.maxHeight), roundToMeasurementIncrement(2400 / 25.4, increment, 'down')),
      storefrontSizePolicy: 'NDR-D-040-provisional-all-controls-mounts',
    };
  }

  function resolveMeasurementRules(configuration, rules) {
    const base = applyStorefrontSizeCaps(rules?.measurement);
    const confirmedEnvelope = base.confirmedSizeEnvelope;
    const selectedHeadrail = configuration?.options?.headrail_style;
    const controlId = configuration?.control;
    const controlEnvelope = confirmedEnvelope?.controls?.[controlId];
    const coveredHeadrails = confirmedEnvelope?.headrailStyles || [confirmedEnvelope?.headrailStyle];
    if (!confirmedEnvelope || !coveredHeadrails.includes(selectedHeadrail) || !controlEnvelope) return base;

    const increment = Number(base.increment || 0.125);
    const millimetersToInches = (millimeters) => Number(millimeters) / 25.4;
    const minWidth = controlEnvelope.minWidthMm === undefined
      ? base.minWidth
      : Math.max(base.minWidth, minimumOfferedWidth(controlEnvelope.minWidthMm, increment));
    const maxWidth = controlEnvelope.maxWidthMm === undefined
      ? base.maxWidth
      : Math.min(base.maxWidth, roundToMeasurementIncrement(millimetersToInches(controlEnvelope.maxWidthMm), increment, 'down'));
    const minHeight = controlEnvelope.minHeightMm === undefined
      ? base.minHeight
      : Math.max(base.minHeight, roundToMeasurementIncrement(millimetersToInches(controlEnvelope.minHeightMm), increment, 'up'));
    const recommendedMaxHeight = controlEnvelope.heightQualifier === 'recommended-maximum' && controlEnvelope.maxHeightMm !== undefined
      ? Math.min(base.maxHeight, roundToMeasurementIncrement(millimetersToInches(controlEnvelope.maxHeightMm), increment, 'down'))
      : null;
    const maxHeight = controlEnvelope.maxHeightMm === undefined || recommendedMaxHeight !== null
      ? base.maxHeight
      : Math.min(base.maxHeight, roundToMeasurementIncrement(millimetersToInches(controlEnvelope.maxHeightMm), increment, 'down'));

    return {
      ...base,
      minWidth,
      maxWidth,
      minHeight,
      maxHeight,
      recommendedMaxHeight,
      appliedControlEnvelope: {
        controlId,
        headrailStyle: selectedHeadrail,
        evidenceStatus: confirmedEnvelope.evidenceStatus,
        previewExtensionStatus: selectedHeadrail === confirmedEnvelope.headrailStyle
          ? ''
          : (confirmedEnvelope.previewExtensionStatus || ''),
        heightQualifier: controlEnvelope.heightQualifier || '',
      },
    };
  }

  function requiresMeasurementReconfirmation(previousConfiguration, nextConfiguration) {
    if (!previousConfiguration || !nextConfiguration) return false;
    return previousConfiguration.control !== nextConfiguration.control
      || previousConfiguration.options?.headrail_style !== nextConfiguration.options?.headrail_style;
  }


  function validateOpeningMaterial(material, cautionAcknowledged) {
    const normalized = String(material || '').trim().toLowerCase();
    if (!normalized) return { valid: false, error: 'Pick the material at the two mount points.' };
    const allowedMaterials = new Set([
      'drywall-plaster',
      'wood',
      'metal',
      'vinyl',
      'concrete-masonry',
      'tile-stone',
      'other',
    ]);
    if (!allowedMaterials.has(normalized)) {
      return { valid: false, error: 'Pick a material from the list for the two mount points.' };
    }

    if (normalized === 'vinyl') {
      const warning = 'PVC or vinyl surfaces need an extra check. These surfaces and their joints can bend. No-drill hardware presses on them.';
      if (!cautionAcknowledged) return { valid: false, requiresCaution: true, error: `${warning} Confirm that you have read this warning to go on.` };
      return { valid: true, requiresCaution: true, warning };
    }

    return { valid: true, warning: '' };
  }

  function validateMount(mountSystem, mountType, rules) {
    const mount = rules.mounts && rules.mounts[mountSystem];

    if (!mount) {
      return { valid: false, error: 'Choose a mount.' };
    }

    if (!mount.enabled) {
      return { valid: false, error: `You cannot buy ${mount.label} yet.` };
    }

    if (!mount.allowedMountTypes || !mount.allowedMountTypes.includes(mountType)) {
      if (mountSystem === 'tension-inside') {
        return { valid: false, error: 'No-drill shades hold by pressure, so they need inside mount.' };
      }
      return { valid: false, error: `${mount.label} does not work with ${mountType} mount.` };
    }

    return { valid: true, mount };
  }

  function findPriceBand(width, height, priceBands) {
    const numericWidth = Number(width);
    const numericHeight = Number(height);

    if (!Number.isFinite(numericWidth) || !Number.isFinite(numericHeight)) return null;

    return (
      (priceBands || []).find(
        (band) => numericWidth <= Number(band.maxWidth) && numericHeight <= Number(band.maxHeight),
      ) || null
    );
  }

  function validateControl(controlId, options, rules) {
    if (['pull_chain', 'cord'].includes(controlId) || options?.chain_material) {
      return { valid: false, error: 'Choose a cordless or motorized control from the list.' };
    }
    const control = rules.controls && rules.controls[controlId];
    if (!control || !control.enabled) {
      return { valid: false, error: 'Choose a control from the list.' };
    }
    const compatible = !control.requires || Object.entries(control.requires).every(
      ([id, allowed]) => allowed.includes((options || {})[id]),
    );
    if (!compatible) {
      return { valid: false, error: `${control.label} does not work with this headrail.` };
    }

    const childOptionId = control.requiresChildOption;
    const selectedChildId = childOptionId ? (options || {})[childOptionId] : '';
    if (childOptionId && !control.childOptions?.[selectedChildId]) {
      return { valid: false, error: `Choose a ${childOptionId.replaceAll('_', ' ')} for ${control.label}.` };
    }
    const staleChildOptionId = Object.values(rules.controls || {})
      .map((candidate) => candidate.requiresChildOption)
      .find((candidateId) => candidateId && candidateId !== childOptionId && (options || {})[candidateId]);
    if (staleChildOptionId) {
      return { valid: false, error: `${staleChildOptionId.replaceAll('_', ' ')} only works with the control it belongs to.` };
    }
    return { valid: true, control, childOption: control.childOptions?.[selectedChildId] || null };
  }

  function validateUpgrades(upgradeIds, controlId, rules, mountSystem) {
    const errors = [];
    const upgrades = [];

    for (const upgradeId of upgradeIds || []) {
      const upgrade = rules.upgrades && rules.upgrades[upgradeId];

      if (!upgrade) {
        errors.push(`You cannot use this upgrade. Please pick another one.`);
        continue;
      }

      if (!upgrade.enabled) {
        errors.push(`You cannot use ${upgrade.label}.`);
        continue;
      }

      if (upgrade.requiresControls && !upgrade.requiresControls.includes(controlId)) {
        const requiredLabels = upgrade.requiresControls
          .map((id) => (rules.controls[id] && rules.controls[id].label) || id)
          .join(' or ');
        errors.push(`${upgrade.label} needs ${requiredLabels}.`);
        continue;
      }

      if (mountSystem && upgrade.allowedMounts && !upgrade.allowedMounts.includes(mountSystem)) {
        errors.push(`You cannot use ${upgrade.label} with this mount.`);
        continue;
      }

      upgrades.push(upgrade);
    }

    return { valid: errors.length === 0, errors, upgrades };
  }

  function reconcileInstallationMountTransition(previousOptions, requestedOptions) {
    const previous = previousOptions || {};
    const options = { ...(requestedOptions || {}) };
    const installationChanged = previous.installation_method !== options.installation_method;

    delete options.installation_surface;

    if (options.installation_method === 'no-drill') {
      options.mount_location = 'inside';
    } else if (options.installation_method === 'standard-bracket') {
      // Decision A (owner 2026-08-18): Standard Bracket is an outside-mount lane.
      // Face/Ceiling installer orientation is not a customer product decision and
      // installation surface is retired.
      options.mount_location = 'outside';
    } else {
      options.mount_location = '';
    }

    return {
      options,
      installationChanged,
      mountChanged: previous.mount_location !== options.mount_location || installationChanged,
    };
  }

  function deriveBottomRailFinish(options, rules) {
    const selected = options || {};
    if (selected.bottom_rail !== 'regular') return '';
    const group = (rules?.optionGroups || []).find((candidate) => candidate.id === 'bottom_rail_finish');
    if (!group?.derivedFrom) return '';
    const isDerivedForSelection = !group.derivedWhen || Object.entries(group.derivedWhen).every(
      ([optionId, allowed]) => Array.isArray(allowed) && allowed.includes(selected[optionId]),
    );
    if (!isDerivedForSelection) return null;
    const sourceId = group?.derivedFrom;
    const sourceValue = sourceId ? selected[sourceId] : '';
    const derived = group?.derivedOptionMap?.[sourceValue] || '';
    return (group?.options || []).some((option) => option.id === derived) ? derived : '';
  }

  function validateAccessories(accessoryIds, quantities, controlId, mountSystem, options, rules) {
    const errors = [];
    const accessories = [];
    const selectedOptions = options || {};
    const selectedQuantities = quantities || {};
    const matches = (conditions) => !conditions || Object.entries(conditions).every(
      ([id, allowed]) => allowed.includes(selectedOptions[id]),
    );

    for (const accessoryId of accessoryIds || []) {
      const accessory = rules.accessories && rules.accessories[accessoryId];
      if (!accessory || !accessory.enabled) {
        errors.push(`You cannot use this add-on. Please choose another option.`);
        continue;
      }
      if (accessory.requiresControls && !accessory.requiresControls.includes(controlId)) {
        errors.push(`You cannot use ${accessory.label} with this control.`);
        continue;
      }
      if (accessory.allowedMounts && !accessory.allowedMounts.includes(mountSystem)) {
        errors.push(`You cannot use ${accessory.label} with this mount.`);
        continue;
      }
      if (!matches(accessory.requires)) {
        errors.push(`You cannot use ${accessory.label} with these choices.`);
        continue;
      }
      const quantity = Number(selectedQuantities[accessoryId] ?? 1);
      const maximum = Number(accessory.maxQuantity || 1);
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > maximum) {
        errors.push(`${accessory.label}: choose from 1 to ${maximum}.`);
        continue;
      }
      accessories.push({ id: accessoryId, quantity, ...accessory });
    }

    return { valid: errors.length === 0, errors, accessories };
  }

  function getFinishCoordinationWarning(options, rules) {
    const selected = options || {};
    const groups = rules?.optionGroups || [];
    const headrailGroup = groups.find((group) => group.id === 'headrail_style');
    const headrailFinishGroup = groups.find((group) => group.id === 'hardware_finish');
    const bottomFinishGroup = groups.find((group) => group.id === 'bottom_rail_finish');
    const headrail = headrailGroup?.options?.find((option) => option.id === selected.headrail_style);
    const headrailFinish = headrailFinishGroup?.options?.find((option) => option.id === selected.hardware_finish);
    const bottomRailFinish = bottomFinishGroup?.options?.find((option) => option.id === selected.bottom_rail_finish);
    const conditionsMatch = (conditions) => !conditions || Object.entries(conditions).every(
      ([id, allowed]) => allowed.includes(selected[id]),
    );
    const headrailFinishChoices = (headrailFinishGroup?.options || []).filter((option) => (
      conditionsMatch(headrailFinishGroup?.activeWhen)
      && conditionsMatch(option.requires)
    ));
    const headrailFinishIsSelectable = Boolean(
      headrailFinish
      && conditionsMatch(headrailFinishGroup?.activeWhen)
      && conditionsMatch(headrailFinish.requires)
      && (!headrailFinishGroup?.hideWhenSingleOption || headrailFinishChoices.length > 1)
    );
    const bottomRailFinishIsSelectable = Boolean(
      bottomRailFinish
      && conditionsMatch(bottomFinishGroup?.activeWhen)
      && conditionsMatch(bottomRailFinish.requires)
    );
    const headrailCanonical = headrailFinish?.canonicalFinishId;
    const bottomCanonical = bottomRailFinish?.canonicalFinishId;
    const active = Boolean(
      headrail
      && !headrail.fabricWrapped
      && headrailFinishIsSelectable
      && bottomRailFinishIsSelectable
      && headrailCanonical
      && bottomCanonical
      && headrailCanonical !== bottomCanonical
    );
    return {
      active,
      headrailFinishLabel: headrailFinish?.label || '',
      bottomRailFinishLabel: bottomRailFinish?.label || '',
      headrailCanonicalFinishId: headrailCanonical || '',
      bottomRailCanonicalFinishId: bottomCanonical || '',
    };
  }

  function calculatePrice(configuration, rules) {
    const errors = [];
    if (rules.measurement?.status === 'unresolved') {
      return { valid: false, errors: ['Sizing is not available for this shade yet.'] };
    }
    const measurementRules = resolveMeasurementRules(configuration, rules);
    const widthResult = validateMeasurement(configuration.width, 'width', measurementRules);
    const heightResult = validateMeasurement(configuration.height, 'height', measurementRules);
    const mountResult = validateMount(configuration.mountSystem, configuration.mountType, rules);
    const controlResult = validateControl(configuration.control, configuration.options, rules);
    const upgradeResult = validateUpgrades(
      configuration.upgrades || [],
      configuration.control,
      rules,
      configuration.mountSystem,
    );
    const accessoryResult = validateAccessories(
      configuration.accessories || [],
      configuration.accessoryQuantities || {},
      configuration.control,
      configuration.mountSystem,
      configuration.options,
      rules,
    );

    if (!widthResult.valid) errors.push(widthResult.error);
    if (!heightResult.valid) errors.push(heightResult.error);
    if (!mountResult.valid) errors.push(mountResult.error);
    if (!controlResult.valid) errors.push(controlResult.error);
    if (!upgradeResult.valid) errors.push(...upgradeResult.errors);
    if (!accessoryResult.valid) errors.push(...accessoryResult.errors);

    const priceBand =
      widthResult.valid && heightResult.valid
        ? findPriceBand(widthResult.value, configuration.height, rules.priceBands)
        : null;

    if (!priceBand && widthResult.valid && heightResult.valid) {
      errors.push('There is no price for this size.');
    }

    if (errors.length > 0) {
      return { valid: false, errors };
    }

    const baseCents = Number(priceBand.baseCents || 0);
    const controlCents = Number(controlResult.control.surchargeCents || 0);
    const mountCents = Number(mountResult.mount.surchargeCents || 0);
    const upgradeCents = upgradeResult.upgrades.reduce(
      (sum, upgrade) => sum + Number(upgrade.surchargeCents || 0),
      0,
    );
    const accessoryCents = accessoryResult.accessories.reduce(
      (sum, accessory) => sum + (Number(accessory.surchargeCents || 0) * accessory.quantity),
      0,
    );
    const variantId = priceBand.variantIds && priceBand.variantIds[configuration.control];

    if (!variantId && rules.checkoutEnabled) {
      return {
        valid: false,
        errors: ['You cannot order this size with this control.'],
      };
    }

    return {
      valid: true,
      errors: [],
      baseCents,
      controlCents,
      mountCents,
      upgradeCents,
      accessoryCents,
      totalCents: baseCents + controlCents + mountCents + upgradeCents + accessoryCents,
      productionWidth: widthResult.value,
      variantId,
      priceBandId: priceBand.id,
    };
  }

  function formatInches(value) {
    const normalizedEighths = Math.round(Number(value) * 8);
    const whole = Math.floor(normalizedEighths / 8);
    const eighths = ((normalizedEighths % 8) + 8) % 8;
    const fraction = FRACTIONS.find((item) => item.eighths === eighths);

    return `${whole}${fraction && fraction.label ? ` ${fraction.label}` : ''} in`;
  }

  function normalizeOptionIdentity(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  function sanitizeOptionSummary(optionSummary) {
    if (!Array.isArray(optionSummary)) return optionSummary;
    return optionSummary.filter((option) => (
      normalizeOptionIdentity(option?.id) !== 'installation_surface'
      && normalizeOptionIdentity(option?.label) !== 'installation_surface'
      && normalizeOptionIdentity(option?.id) !== 'chain_material'
      && normalizeOptionIdentity(option?.label) !== 'chain_material'
    ));
  }

  function requiresNoDrillInsideDepth(configuration) {
    const options = configuration?.options || {};
    const hasExplicitInstallation = Object.prototype.hasOwnProperty.call(options, 'installation_method')
      || Object.prototype.hasOwnProperty.call(options, 'mount_location');
    if (hasExplicitInstallation) {
      return options.installation_method === 'no-drill' && options.mount_location === 'inside';
    }
    const mountSystem = String(configuration?.mountSystem || '').toLowerCase();
    const noDrillMount = /(?:^|[-_])(?:no[-_]?drill|tension)(?:[-_]|$)/.test(mountSystem);
    return configuration?.mountType === 'inside' && noDrillMount;
  }

  function buildLineItemProperties(configuration) {
    const productionWidth = Number.isFinite(Number(configuration.productionWidth))
      ? Number(configuration.productionWidth)
      : Number(configuration.width);
    const openingMaterial = configuration.openingMaterialLabel || configuration.openingMaterial || 'Not recorded';
    const mountingSurface = configuration.mountingSurfaceLabel || configuration.mountingSurface || 'Not recorded';
    const materialCode = configuration.options?.fabric_color;
    const cautionRequired = /pvc|vinyl/i.test(openingMaterial);
    const noDrillDepthRequired = requiresNoDrillInsideDepth(configuration);
    const depthPropertyApplicable = noDrillDepthRequired
      || Boolean(configuration.options?.installation_method);

    const optionProperties = {};
    const safeOptionSummary = sanitizeOptionSummary(configuration.optionSummary);
    if (Array.isArray(safeOptionSummary) && safeOptionSummary.length > 0) {
      safeOptionSummary.forEach((option) => {
        if (!option || !option.label) return;
        const propertyLabel = option.id === 'installation_method'
          ? 'Installation Method'
          : option.id === 'mount_location'
            ? 'Mount Location'
            : option.label;
        optionProperties[propertyLabel] = option.value;
      });
    } else {
      optionProperties.Color = configuration.color;
      optionProperties['Light Control'] = configuration.lightControl;
    }
    if (configuration.categoryLabel) optionProperties.Category = configuration.categoryLabel;
    const measurementConfirmationProperty = configuration.mountType === 'outside'
      ? { 'Outside-Mount Size Confirmed': configuration.measurementConfirmed ? 'Yes' : 'No' }
      : { 'Three-Point Measurement Confirmed': configuration.measurementConfirmed ? 'Yes' : 'No' };
    const selectableAdditions = Array.isArray(configuration.accessoryLabels)
      ? {
          Accessories: configuration.accessoryLabels.length
            ? configuration.accessoryLabels.join(', ')
            : 'None',
        }
      : {
          Upgrades:
            configuration.upgradeLabels && configuration.upgradeLabels.length > 0
              ? configuration.upgradeLabels.join(', ')
              : 'None',
        };
    const orderAccessoryQuantities = normalizeOrderAccessoryQuantities(configuration.orderAccessoryQuantities);
    const orderAccessoryProperties = configuration.orderAccessoryQuantities
      ? {
          'Order Remote · One Channel': String(orderAccessoryQuantities.one_channel_remote),
          'Order Remote · Six Channel': String(orderAccessoryQuantities.six_channel_remote),
          'Order Remote · Sixteen Channel': String(orderAccessoryQuantities.sixteen_channel_remote),
          'Compatible Remote Confirmed': orderAccessoryQuantities.compatible_remote_acknowledged ? 'Yes' : 'No',
        }
      : {};

    return {
      ...optionProperties,
      ...(!Object.prototype.hasOwnProperty.call(optionProperties, 'Installation Method')
        && (configuration.installationMethodLabel || configuration.options?.installation_method)
        ? { 'Installation Method': configuration.installationMethodLabel || configuration.options.installation_method }
        : {}),
      ...(!Object.prototype.hasOwnProperty.call(optionProperties, 'Mount Location')
        && (configuration.mountLocationLabel || configuration.options?.mount_location)
        ? { 'Mount Location': configuration.mountLocationLabel || configuration.options.mount_location }
        : {}),
      Mount: configuration.mountSystemLabel,
      'Production Width': formatInches(productionWidth),
      Height: formatInches(configuration.height),
      'Mounting Surface': mountingSurface,
      'Mounting Point Material': openingMaterial,
      'Surface Caution Acknowledged': cautionRequired
        ? (configuration.openingMaterialCautionAcknowledged ? 'Yes' : 'No')
        : 'Not required',
      ...(depthPropertyApplicable
        ? {
            '2.75-Inch Inside-Mount Depth Confirmed': noDrillDepthRequired
              ? (configuration.noDrillDepthConfirmed ? 'Yes' : 'No')
              : 'Not required',
          }
        : {}),
      Room: configuration.room,
      'Room / Window Label': configuration.roomLabel,
      Control: configuration.controlLabel,
      ...(['pull_chain', 'motorized'].includes(configuration.control)
        ? { 'Control side': configuration.controlSide === 'left' ? 'Left' : 'Right' }
        : {}),
      ...selectableAdditions,
      ...orderAccessoryProperties,
      ...measurementConfirmationProperty,
      'Compatibility Confirmed': configuration.compatibilityConfirmed ? 'Yes' : 'No',
      ...(materialCode ? { _material_code: materialCode } : {}),
      _configuration_id: configuration.configurationId,
      _price_band: configuration.priceBandId,
    };
  }

  function groupConfigurationsByRoom(configurations) {
    return (configurations || []).reduce((groups, configuration) => {
      const room = String(configuration.room || 'Unassigned').trim() || 'Unassigned';
      if (!groups[room]) groups[room] = [];
      groups[room].push(configuration);
      return groups;
    }, {});
  }

  function summarizeOrder(configurations, orderRules) {
    const items = configurations || [];
    const subtotalCents = items.reduce((sum, item) => sum + Number(item.totalCents || 0), 0);
    const tiers = [...(orderRules?.discountTiers || [])].sort(
      (a, b) => Number(b.minQuantity) - Number(a.minQuantity),
    );
    const tier = tiers.find((candidate) => items.length >= Number(candidate.minQuantity));
    const discountPercent = tier ? Number(tier.percent || 0) : 0;
    const discountCents = Math.round((subtotalCents * discountPercent) / 100);
    return {
      quantity: items.length,
      subtotalCents,
      discountPercent,
      discountCents,
      totalCents: subtotalCents - discountCents,
    };
  }

  function normalizeOrderAccessoryQuantities(quantities) {
    const normalizeQuantity = (value) => Math.max(0, Math.floor(Number(value) || 0));
    const source = quantities || {};
    return {
      one_channel_remote: normalizeQuantity(source.one_channel_remote ?? source.oneChannelRemoteQuantity),
      six_channel_remote: normalizeQuantity(source.six_channel_remote ?? source.sixChannelRemoteQuantity),
      sixteen_channel_remote: normalizeQuantity(source.sixteen_channel_remote ?? source.sixteenChannelRemoteQuantity),
      compatible_remote_acknowledged: Boolean(
        source.compatible_remote_acknowledged ?? source.compatibleRemoteAcknowledgement ?? source.compatibleRemoteAcknowledged,
      ),
    };
  }

  function summarizeMotorizedRemoteRequirement(configurations, quantities) {
    const normalized = normalizeOrderAccessoryQuantities(quantities);
    const motorizedShadeCount = (configurations || []).filter(
      (configuration) => configuration?.control === 'motorized',
    ).length;
    const totalRemoteQuantity = normalized.one_channel_remote + normalized.six_channel_remote + normalized.sixteen_channel_remote;
    const requiresExplicitAcknowledgement = motorizedShadeCount > 0 && totalRemoteQuantity === 0;
    const valid = motorizedShadeCount === 0
      || totalRemoteQuantity > 0
      || normalized.compatible_remote_acknowledged;
    return {
      motorizedShadeCount,
      oneChannelRemoteQuantity: normalized.one_channel_remote,
      sixChannelRemoteQuantity: normalized.six_channel_remote,
      sixteenChannelRemoteQuantity: normalized.sixteen_channel_remote,
      totalRemoteQuantity,
      compatibleRemoteAcknowledged: normalized.compatible_remote_acknowledged,
      requiresExplicitAcknowledgement,
      valid,
      message: valid
        ? ''
        : 'Before you review this Motorized order, add at least one remote. Or confirm that you already own a remote that works with it.',
    };
  }

  function validateOptionGroups(options, optionGroups) {
    const errors = [];
    const selected = options || {};
    const matches = (conditions) => !conditions || Object.entries(conditions).every(
      ([id, allowed]) => allowed.includes(selected[id]),
    );
    (optionGroups || []).forEach((group) => {
      const active = matches(group.activeWhen);
      const conditionallyRequired = matches(group.requiredWhen) && Boolean(group.requiredWhen);
      const selectedValue = selected[group.id];
      if (active && (group.required || conditionallyRequired) && !selectedValue) {
        errors.push(`Choose ${String(group.label || group.id).toLowerCase()}.`);
        return;
      }
      if (!selectedValue || !Array.isArray(group.options)) return;
      const selectedOption = group.options.find((option) => option.id === selectedValue);
      if (!active || !selectedOption || !matches(selectedOption.requires)) {
        errors.push(`You cannot use ${String(group.label || group.id)} with these choices.`);
      }
    });
    return { valid: errors.length === 0, errors };
  }

  function sideChannelsAvailable(configuration, rules) {
    return rules?.mode === 'catalog-v2' && rules.id === 'roller-shades'
      && /^NDR-V2-RL-BO-\d{3}$/.test(configuration?.options?.fabric_family || '');
  }

  function validateRollerAddons(configuration, rules) {
    const errors = [];
    if (rules?.mode !== 'catalog-v2' || rules.id !== 'roller-shades') return { valid: true, errors };
    const sideChannel = configuration.sideChannel || 'none';
    if (!['none', 'l_channel', 'u_channel'].includes(sideChannel)) {
      errors.push('Choose a side channel from the list.');
    } else if (sideChannel !== 'none' && !sideChannelsAvailable(configuration, rules)) {
      errors.push('You can use side channels only with blackout shades.');
    }
    if (configuration.control === 'motorized') {
      const type = configuration.remoteType;
      if (!['one_channel', 'six_channel', 'sixteen_channel', 'none'].includes(type)) {
        errors.push('Choose a remote, or choose no remote with this shade.');
      } else if (type === 'none') {
        if (configuration.compatibleRemoteAcknowledgement !== true) {
          errors.push('Pair this shade with a remote in this order. Or use a No Drill remote you already own. Confirm that you will do this.');
        }
      } else if (!Number.isInteger(configuration.remoteQuantity) || configuration.remoteQuantity < 1 || configuration.remoteQuantity > 5) {
        errors.push('Remote quantity must be a whole number from 1 to 5.');
      }
    } else if (configuration.remoteType && configuration.remoteType !== 'none') {
      errors.push('You can use remotes only with motorized control.');
    }
    return { valid: errors.length === 0, errors };
  }

  function validateConfiguration(configuration, rules) {
    const errors = [...validateRollerAddons(configuration, rules).errors];

    if (Array.isArray(rules.optionGroups)) {
      errors.push(...validateOptionGroups(configuration.options, rules.optionGroups).errors);
    } else {
      if (!configuration.color) errors.push('Choose a color.');
      if (!configuration.lightControl) errors.push('Choose a light-control option.');
    }
    if (!rules.steppedBuilder && !configuration.room) errors.push('Choose a room.');
    if (!rules.steppedBuilder && (!configuration.roomLabel || !String(configuration.roomLabel).trim())) {
      errors.push('Add a room or window label.');
    }
    if (!configuration.control) errors.push('Choose a control.');
    const needsSurface = !rules.steppedBuilder || requiresNoDrillInsideDepth(configuration);
    if (needsSurface && !['flat', 'textured'].includes(configuration.mountingSurface)) {
      errors.push('Pick a flat or textured surface to mount on.');
    }

    const measurementPending = rules.measurement?.status === 'unresolved';
    const measurementRules = resolveMeasurementRules(configuration, rules);
    const widthResult = measurementPending
      ? null
      : validateMeasurement(configuration.width, 'width', measurementRules);
    const heightResult = measurementPending
      ? null
      : validateMeasurement(configuration.height, 'height', measurementRules);
    const mountResult = validateMount(configuration.mountSystem, configuration.mountType, rules);
    const openingResult = validateOpeningMaterial(
      configuration.openingMaterial,
      configuration.openingMaterialCautionAcknowledged,
    );
    const upgradeResult = validateUpgrades(
      configuration.upgrades || [],
      configuration.control,
      rules,
      configuration.mountSystem,
    );
    const accessoryResult = validateAccessories(
      configuration.accessories || [],
      configuration.accessoryQuantities || {},
      configuration.control,
      configuration.mountSystem,
      configuration.options,
      rules,
    );
    const controlResult = configuration.control
      ? validateControl(configuration.control, configuration.options, rules)
      : null;

    if (measurementPending) {
      errors.push('Sizing is not available for this shade yet.');
    } else {
      if (!widthResult.valid) errors.push(widthResult.error);
      if (!heightResult.valid) errors.push(heightResult.error);
    }
    if (!mountResult.valid) errors.push(mountResult.error);
    if (needsSurface && !openingResult.valid) errors.push(openingResult.error);
    if (!upgradeResult.valid) errors.push(...upgradeResult.errors);
    if (!accessoryResult.valid) errors.push(...accessoryResult.errors);
    if (controlResult && !controlResult.valid) errors.push(controlResult.error);
    if (requiresNoDrillInsideDepth(configuration) && !configuration.noDrillDepthConfirmed) {
      errors.push('Confirm that the opening has at least 2¾ inches of clear depth.');
    }
    if (!measurementPending && !configuration.measurementConfirmed) {
      errors.push(configuration.mountType === 'outside'
        ? 'Confirm the size of the finished shade. Check what it will cover and that there is room to mount it.'
        : 'Make sure you measured in three places and picked the smallest width. Do not round.');
    }
    if (!configuration.compatibilityConfirmed) errors.push('Confirm that the shade will work with your window.');

    return { valid: errors.length === 0, errors };
  }

  return {
    normalizeMeasurement,
    roundToMeasurementIncrement,
    resolveMeasurementRules,
    applyStorefrontSizeCaps,
    requiresMeasurementReconfirmation,
    validateMeasurement,
    validateOpeningMaterial,
    validateMount,
    findPriceBand,
    validateControl,
    validateUpgrades,
    reconcileInstallationMountTransition,
    deriveBottomRailFinish,
    validateAccessories,
    getFinishCoordinationWarning,
    calculatePrice,
    formatInches,
    sanitizeOptionSummary,
    requiresNoDrillInsideDepth,
    buildLineItemProperties,
    groupConfigurationsByRoom,
    summarizeOrder,
    normalizeOrderAccessoryQuantities,
    summarizeMotorizedRemoteRequirement,
    validateOptionGroups,
    sideChannelsAvailable,
    validateRollerAddons,
    validateConfiguration,
  };
});
